// Лентата „Плащане" в документа на доставка (под заглавката, не се печата):
// какво е плащането и бутоните според него — виж `deliveryPaymentState.ts`.
// Бутоните идват само когато документът е отворен от ред (подадени handler-и).
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { Delivery } from "../../../../api/inventory";
import { SECONDARY_BUTTON_CLASS } from "../../components/list/buttonStyles";
import { paymentPanelOf } from "./deliveryPaymentState";

type Props = {
  delivery: Delivery;
  onMarkPaid?: () => void;
  onChange?: () => void;
  onMarkUnpaid?: () => void;
  className?: string;
};

const shortDate = (iso: string) => dayjs(iso).format("DD.MM.YYYY");

export const DeliveryPaymentPanel = ({ delivery, onMarkPaid, onChange, onMarkUnpaid, className = "" }: Props) => {
  const { t } = useTranslation();
  const state = paymentPanelOf(delivery);
  if (!state) return null;

  const paidParts = () =>
    [
      state.paidAt ? t("inventoryPage.payPanelPaid", { date: shortDate(state.paidAt) }) : t("inventoryPage.payPaid"),
      state.method ? t(`inventoryPage.payMethod_${state.method}`) : null,
      state.reference ? t("inventoryPage.payPanelReference", { reference: state.reference }) : null,
      state.paidByName ? t("inventoryPage.payPanelMarkedBy", { name: state.paidByName }) : null,
    ].filter(Boolean);

  let text: string;
  let tone = "text-body";
  switch (state.kind) {
    case "paid":
      text = paidParts().join(" · ");
      tone = "text-ok-deep";
      break;
    case "unpaid":
      text =
        state.ageDays > 0
          ? t("inventoryPage.payPanelUnpaid", { count: state.ageDays })
          : t("inventoryPage.payPanelUnpaidToday");
      tone = "text-danger-ink";
      break;
    case "byInvoice":
      text = [
        t("inventoryPage.payPanelByInvoice"),
        state.paidAt ? t("inventoryPage.payPanelPaidSeparately", { date: shortDate(state.paidAt) }) : null,
      ]
        .filter(Boolean)
        .join(" · ");
      break;
    case "annulled":
      text = [t("inventoryPage.payPanelAnnulled"), ...(state.paidAt ? paidParts() : [])].join(" · ");
      break;
    default:
      text = t("inventoryPage.payUntracked");
  }

  const buttonClass = `${SECONDARY_BUTTON_CLASS} !py-1.5`;
  return (
    <div
      className={`print:hidden flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[9px] border border-shade-2 bg-fill px-3.5 py-2 text-[13.5px] leading-[1.45] ${className}`}
    >
      <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted">
        {t("inventoryPage.colPayment")}
      </span>
      <span className={`min-w-0 flex-1 ${tone}`}>{text}</span>
      {state.canChange && onChange && (
        <button type="button" onClick={onChange} className={buttonClass}>
          {t("inventoryPage.payChange")}
        </button>
      )}
      {state.canMarkUnpaid && onMarkUnpaid && (
        <button type="button" onClick={onMarkUnpaid} className={buttonClass}>
          {t("inventoryPage.payMarkUnpaid")}
        </button>
      )}
      {state.canMarkPaid && onMarkPaid && (
        <button type="button" onClick={onMarkPaid} className={buttonClass}>
          {t("inventoryPage.payMarkPaidOne")}
        </button>
      )}
    </div>
  );
};

export default DeliveryPaymentPanel;
